import { zodResolver } from '@hookform/resolvers/zod';
import { JSX, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Autocomplete,
  FileMimeTypes,
  FilePicker,
  ItemStatus,
} from '@/modules/common';
import { FileObject } from '@/modules/common/types';

import { useModelAttributes } from '../api';
import { maxModelArchiveSize } from '../constants';
import { getAllModelOptions } from '../helpers';
import { ModelFormSchema, ModelFormValues } from '../validations';

interface ModelFormProps {
  editMode?: boolean;
  defaultValues?: ModelFormValues;
  fileObject?: FileObject | null;
  onSubmit: (data: ModelFormValues) => Promise<void>;
  children: JSX.Element;
}

const defaultFormValues = {
  name: '',
  colorId: '',
  typeId: '',
  genderId: '',
  status: '',
  updatedAt: undefined,
  archiveUrl: '',
};

export function ModelForm({
  onSubmit,
  children,
  editMode,
  defaultValues,
  fileObject,
}: ModelFormProps) {
  const { data: options, restrictions } = useModelAttributes();

  const form = useForm<ModelFormValues>({
    resolver: zodResolver(ModelFormSchema),
    defaultValues: defaultFormValues,
  });

  const fileName = form.watch('fileNameId');

  const fileNameRestrictions = useMemo(() => {
    return restrictions.filter((r) => r.fileName.id === fileName);
  }, [fileName, restrictions]);

  const availableOptions = useMemo(
    () => getAllModelOptions(fileNameRestrictions),
    [fileNameRestrictions]
  );

  useEffect(() => {
    if (!fileName || !availableOptions) return;

    const fields = [
      { key: 'colorId' as const, data: availableOptions.color },
      { key: 'typeId' as const, data: availableOptions.type },
      { key: 'genderId' as const, data: availableOptions.gender },
    ];

    fields.forEach(({ key, data }) => {
      const formValue = form.getValues(key);
      const hasOtherValue = !data.some((option) => option.id === formValue);

      const newValue =
        data.length === 1 ? data[0].id : !hasOtherValue ? formValue : '';

      form.setValue(key, newValue);
    });
  }, [fileName, availableOptions]);

  useEffect(() => {
    if (defaultValues) {
      form.reset(defaultValues);
    }
  }, [defaultValues]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        {children}
        <div className="flex flex-col gap-10 max-w-5xl">
          <div className="grid grid-flow-row-dense grid-cols-1 lg:grid-cols-4 gap-6 sm:grid-cols-1">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Model name</FormLabel>
                  <FormControl>
                    <Input placeholder="Model name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Autocomplete
              name="fileNameId"
              label="File name"
              control={form.control}
              options={options.fileNames}
              placeholder="Select file name"
            />

            {fileName && (
              <>
                <Autocomplete
                  name="genderId"
                  label="Gender"
                  control={form.control}
                  options={availableOptions.gender}
                  placeholder="Select gender"
                  isDisabled={availableOptions.gender.length === 1}
                />

                <ItemStatus
                  name="status"
                  isDisabled={!editMode}
                  control={form.control}
                />
                <Autocomplete
                  name="typeId"
                  label="Model type"
                  control={form.control}
                  options={availableOptions.type}
                  placeholder="Select type"
                  isDisabled={availableOptions.type.length === 1}
                />
                <Autocomplete
                  name="colorId"
                  label="Model color"
                  control={form.control}
                  options={availableOptions.color}
                  placeholder="Select color"
                  isDisabled={availableOptions.color.length === 1}
                />
                {editMode && (
                  <FormField
                    control={form.control}
                    name="updatedAt"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Last modified</FormLabel>
                        <FormControl>
                          <Input
                            disabled
                            placeholder="YYYY-MM-DD"
                            required={false}
                            readOnly
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </>
            )}
          </div>

          <div className="flex flex-col gap-4 max-w-md">
            <FilePicker
              label="3D Model"
              name="archiveUrl"
              accept={[FileMimeTypes.Zip]}
              placeholder="Add archive with 3d Model (.usdc file)"
              maxFileSize={maxModelArchiveSize}
              initialFileName={fileObject?.filename}
              initialFileSize={fileObject?.contentLength}
              tips={
                <p className="text-sm text-zinc-500">
                  The archive must be in
                  <span className="text-zinc-100">&nbsp;.zip&nbsp;</span>
                  format, contains exactly one .usdc file and have optional .png
                  textures.
                </p>
              }
            />
          </div>
        </div>
      </form>
    </Form>
  );
}

export default ModelForm;
